import { useEffect, useRef, useState } from 'react';
import { usePreviewLanguage, setPreviewLanguage } from '@/hooks/useLanguagePreview';
import { LANGUAGES } from '@/lib/multilingual';

/**
 * Compact, accessible language selector for the public header.
 *
 * Only English and Spanish are available, and the control is explicitly labelled
 * "Preview" — selecting a language only changes the sample sections that have a
 * translation, it does not translate the whole website.
 */
export default function LanguageSelector() {
  const current = usePreviewLanguage();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const active = LANGUAGES.find((l) => l.code === current) ?? LANGUAGES[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (code: 'en' | 'es') => {
    setPreviewLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Reading language: ${active.label}. Multilingual preview.`}
        className="inline-flex items-center gap-2 h-10 px-2.5 rounded-md border border-background-300 hover:border-foreground-700 text-foreground-800 cursor-pointer transition-colors bg-background-50"
      >
        <span className="w-4 h-4 flex items-center justify-center text-base"><i className="ri-translate-2"></i></span>
        <span className="hidden sm:inline font-label font-bold uppercase tracking-wide text-xs whitespace-nowrap">{active.code.toUpperCase()}</span>
        <span className="w-3.5 h-3.5 flex items-center justify-center text-xs text-foreground-500"><i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i></span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Reading language preview"
          className="absolute right-0 mt-2 w-64 z-50 bg-background-50 border border-background-200 rounded-lg p-2 shadow-none"
        >
          <div className="px-2 pt-1.5 pb-2 border-b border-background-200">
            <span className="inline-flex items-center gap-1.5 bg-accent-100 text-accent-900 border border-accent-300 font-label font-bold uppercase tracking-widest text-[10px] px-2 py-0.5 rounded-sm">
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-flask-line"></i></span>
              Multilingual preview
            </span>
            <p className="mt-2 text-[11px] text-foreground-500 leading-snug">Only selected sample sections include a translation. The whole site is not translated.</p>
          </div>
          <ul className="pt-1.5">
            {LANGUAGES.map((l) => {
              const on = l.code === current;
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={on}
                    onClick={() => choose(l.code)}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left cursor-pointer transition-colors ${on ? 'bg-background-100' : 'hover:bg-background-100'}`}
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-base text-foreground-600">
                      <i className={on ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground-950">{l.native}</span>
                      <span className="block text-[11px] text-foreground-500">{l.label}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}